import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';

function RevealSection({ children, className = '', delay = 0 }) {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const LAST_UPDATED = 'July 2026';

export default function Terms() {
  return (
    <div data-testid="terms-page" className="px-6 lg:px-8 pt-[80px] pb-[100px] md:pt-[120px] md:pb-[120px]">
      <div className="max-w-2xl mx-auto">
        <RevealSection>
          <p className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold mb-4">Legal</p>
          <h1 className="font-inter font-bold text-[38px] md:text-[54px] leading-[1.1] tracking-tight text-km-white">
            Terms of Service
          </h1>
          <p className="mt-6 font-inter font-medium text-sm text-km-gray-5">Last updated: {LAST_UPDATED}</p>
        </RevealSection>

        <RevealSection delay={100}>
          <div className="mt-10 p-[30px] md:p-[50px] rounded-[10px] bg-km-card-gradient border border-km-gray-3 space-y-8 font-inter font-medium text-base text-km-gray-5 leading-[1.5]">

            <section className="space-y-4">
              <p>
                These Terms of Service ("Terms") govern your use of the website kingmakersinc.ca and the products and services offered by King Makers ("we," "us," or "our"), a sole proprietorship registered in Ontario, Canada. By using our site or buying a product, you agree to these Terms. If you do not agree, please do not use the site.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">What we offer</h2>
              <p>
                King Makers provides digital products, such as the High-Stakes Narrative Pack, along with AI systems and consulting engagements. Some offerings are available now, and others are described as in development or coming soon. Descriptions on the site are provided in good faith, and features may evolve as products develop.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Purchases and payment</h2>
              <p>
                Prices are shown in Canadian dollars unless stated otherwise. Payment is processed securely by Stripe. When you complete a purchase, you receive access to the digital product through the delivery method shown on the confirmation page, typically an instant link to a Notion template you can duplicate to your own workspace.
              </p>
              <p>
                You are responsible for any currency conversion or fees your bank or card issuer applies if you are outside Canada.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Refunds</h2>
              <p>
                Because our products are digital and delivered instantly, including access to downloadable or copyable materials, all sales are final. If you experience an access issue, such as a link that does not work or a template you cannot duplicate, contact us within 7 days of purchase and we will resolve it or provide access. If you accidentally purchased more than once, contact us within 24 hours and we will help correct it. Our full{' '}
                <Link to="/refund-policy" className="text-km-gold hover:text-km-gold-deep transition-colors cursor-pointer">Refund Policy</Link>{' '}
                page has the complete details.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Acceptable use</h2>
              <p>
                Our products are licensed to you for your own personal or internal business use. You may not resell, redistribute, publicly share, or repackage our materials for others without our written permission. You agree not to misuse the site, attempt to disrupt it, or access it in a way that breaks any applicable law.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Intellectual property</h2>
              <p>
                All content, materials, frameworks, and products offered by King Makers are our intellectual property or used with permission, and are protected by applicable law. Buying a product grants you a license to use it as described, not ownership of the underlying materials.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">No professional advice guarantee</h2>
              <p>
                Our products, systems, and consulting are provided to support your own decision-making. They do not constitute legal, financial, or other professional advice, and we do not guarantee any particular outcome or result. You remain responsible for the decisions you make and the actions you take.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Limitation of liability</h2>
              <p>
                To the fullest extent permitted by law, King Makers is not liable for any indirect, incidental, or consequential damages arising from your use of the site or our products. Our total liability for any claim relating to a product is limited to the amount you paid for that product.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Third-party links and services</h2>
              <p>
                Our site links to and relies on third-party services, including Stripe, Calendly, and Notion. We are not responsible for the content, availability, or practices of those services, which are governed by their own terms and policies.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Changes to these Terms</h2>
              <p>
                We may update these Terms as our business or the law changes. When we do, we will update the date at the top of this page. Continued use of the site after a change means you accept the updated Terms.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Governing law</h2>
              <p>
                These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada that apply there, without regard to conflict-of-law principles.
              </p>
            </section>

            <div className="pt-8 border-t border-km-gray-3">
              <p className="font-inter font-medium text-km-white">Contact</p>
              <p className="mt-2">
                Questions about these Terms? Email{' '}
                <a href="mailto:hello@kingmakersinc.ca" className="text-km-gold hover:text-km-gold-deep transition-colors cursor-pointer">hello@kingmakersinc.ca</a>.
              </p>
              <p className="mt-2 text-sm text-km-gray-5">King Makers, Mississauga, Ontario, Canada.</p>
            </div>

          </div>
        </RevealSection>

        <RevealSection delay={200}>
          <div className="mt-16 text-center">
            <Link to="/" className="font-inter font-medium text-sm text-km-gray-5 hover:text-km-white transition-colors cursor-pointer">
              &larr; Back to King Makers
            </Link>
          </div>
        </RevealSection>
      </div>
    </div>
  );
}
